import { useMemo } from "react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Tooltip, Text, Button, Icon } from "@chakra-ui/react";
import { FiSettings } from "react-icons/fi";
import { Application } from "@/model/applications";
import { formatDate } from "@/helpers/formatDate";

export type ApplicationRow = Application & { actions: string };

const columnHelper = createColumnHelper<ApplicationRow>();

export const useApplicationColumns = (canManageChannel: boolean, onOpenAssign: (id: number) => void): Array<ColumnDef<ApplicationRow, any>> => {
  return useMemo(() => {
    const base: Array<ColumnDef<ApplicationRow, any>> = [
      columnHelper.accessor("customer.name", {
        id: "customer_name",
        header: "Заказчик",
        size: 160,
        minSize: 120,
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "Название",
        size: 220,
        minSize: 160,
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="200px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("project.name", {
        id: "project.name",
        header: "Проект",
        size: 160,
        minSize: 120,
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("channels", {
        id: "channels",
        header: "Каналы подбора",
        size: 220,
        minSize: 140,
        cell: (info) => {
          const value = info.getValue() as Array<{ name?: string }> | undefined | null;
          if (Array.isArray(value) && value.length > 0) {
            const text = value
              .map((c) => c?.name)
              .filter(Boolean)
              .join(", ");
            return (
              <Tooltip label={text} hasArrow>
                <Text noOfLines={1} maxW="220px">
                  {text}
                </Text>
              </Tooltip>
            );
          }
          return (
            <Tooltip label="Неназначена" hasArrow>
              <Text noOfLines={1} maxW="160px">
                Неназначена
              </Text>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor("profile.name", {
        header: "Профиль",
        id: "profile_name",
        size: 160,
        minSize: 120,
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("status.name", {
        header: "Статус",
        id: "status_name",
        size: 140,
        minSize: 100,
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="140px">
              {info.getValue() || ""}
            </Text>
          </Tooltip>
        ),
      }),
      columnHelper.accessor("candidate_grade", {
        header: "Грейд",
        id: "candidate_grade",
        size: 140,
        minSize: 100,
        cell: (info) => {
          const value = info.getValue() as Array<{ id: number; name: string }> | null | undefined;
          if (!value) return <Text noOfLines={1} maxW="140px"></Text>;
          if (Array.isArray(value)) {
            const text = value
              .map((g) => g?.name)
              .filter(Boolean)
              .join(", ");
            return (
              <Tooltip label={text} hasArrow>
                <Text noOfLines={1} maxW="160px">
                  {text}
                </Text>
              </Tooltip>
            );
          }
          return <Text noOfLines={1} maxW="140px"></Text>;
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Дата создания",
        size: 140,
        minSize: 120,
        cell: (info) => {
          const value = formatDate(info.getValue());
          return (
            <Tooltip label={value} hasArrow>
              <Text noOfLines={1} maxW="140px">
                {value}
              </Text>
            </Tooltip>
          );
        },
        enableColumnFilter: true,
        enableSorting: true,
      }),
    ];
    if (canManageChannel) {
      base.push(
        columnHelper.accessor("actions", {
          header: "Действия",
          size: 80,
          minSize: 60,
          cell: (info) => (
            <Button
              variant="ghost"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenAssign(info.row.original.id);
              }}
            >
              <Icon as={FiSettings as any} boxSize={5} />
            </Button>
          ),
        }),
      );
    }
    return base;
  }, [canManageChannel, onOpenAssign]);
};
